import { ActionTree } from "vuex";
import {
  RecruiterAiInterviewsState,
  ScheduleAiInterviewInvitePayload,
  AiInterviewInvite,
  InterviewInviteType
} from "@/store/modules/recruiter_ai_interviews/interfaces";
import {
  get_interview_invites,
  upsert_interview_invite
} from "@/store/apis/ml_interview";

function makeId(): string {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export const actions: ActionTree<RecruiterAiInterviewsState, any> = {
  async INIT_AI_INTERVIEW_INVITES({ commit, state }) {
    if (state.initialized) return;
    const [aiResp, personResp] = await Promise.all([
      get_interview_invites("ai"),
      get_interview_invites("person")
    ]);
    const normalize = (rows: any[]): AiInterviewInvite[] =>
      rows.map((row: any) => {
        const p = row?.payload || {};
        return {
          id: String(row?.id || makeId()),
          interview_type: (row?.interview_type || p?.interview_type || "ai") as InterviewInviteType,
          job_id: p?.job_id ?? row?.job_id ?? null,
          job_title: p?.job_title ?? row?.job_title ?? null,
          job_role: p?.job_role ?? null,
          candidate_id: p?.candidate_id ?? row?.candidate_id,
          upload_response_id: p?.upload_response_id ?? null,
          candidate_name: p?.candidate_name || "Candidate",
          candidate_role: p?.candidate_role || "Candidate",
          avatar_uri: p?.avatar_uri ?? null,
          interviewer_email: p?.interviewer_email ?? null,
          ms_teams_link: p?.ms_teams_link ?? null,
          ms_teams_meeting_id: p?.ms_teams_meeting_id ?? null,
          ms_teams_join_meeting_id: p?.ms_teams_join_meeting_id ?? null,
          ms_teams_meeting_passcode: p?.ms_teams_meeting_passcode ?? null,
          platform_room_id: p?.platform_room_id ?? null,
          duration_minutes: p?.duration_minutes ?? null,
          scheduled_at: p?.scheduled_at ?? row?.scheduled_at ?? new Date().toISOString(),
          status: (p?.status || row?.status || "scheduled") as any
        } as AiInterviewInvite;
      });
    const invites: AiInterviewInvite[] = [
      ...normalize(aiResp?.data?.data || []),
      ...normalize(personResp?.data?.data || [])
    ];
    commit("SET_AI_INTERVIEW_INVITES", invites);
    commit("SET_AI_INTERVIEW_INVITES_INITIALIZED", true);
  },

  async SCHEDULE_AI_INTERVIEW_INVITE(
    { commit, state, dispatch },
    payload: ScheduleAiInterviewInvitePayload
  ) {
    dispatch("INIT_AI_INTERVIEW_INVITES");
    const interview_type = payload.interview_type || "ai";
    const payloadAny = payload as any;

    const existingInvite = state.invites.find(
      (i) =>
        (i.interview_type || "ai") === interview_type &&
        i.candidate_id === payload.candidate_id &&
        i.job_id === (payload.job_id ?? null) &&
        i.status === "scheduled"
    );
    if (existingInvite) {
      const existingInviteAny = existingInvite as any;
      const platformRoomId =
        payloadAny.platform_room_id ?? existingInviteAny.platform_room_id ?? null;
      const merged = {
        ...existingInvite,
        job_title: payloadAny.job_title ?? existingInviteAny.job_title ?? null,
        job_role: payload.job_role ?? existingInvite.job_role ?? null,
        candidate_name: payload.candidate_name || existingInvite.candidate_name,
        candidate_role: payload.candidate_role || existingInvite.candidate_role,
        avatar_uri: payload.avatar_uri ?? existingInvite.avatar_uri ?? null,
        interviewer_email: payload.interviewer_email ?? existingInvite.interviewer_email ?? null,
        ms_teams_link: payload.ms_teams_link ?? existingInvite.ms_teams_link ?? null,
        ms_teams_meeting_id:
          payload.ms_teams_meeting_id ?? existingInvite.ms_teams_meeting_id ?? null,
        ms_teams_join_meeting_id:
          payloadAny.ms_teams_join_meeting_id ??
          existingInviteAny.ms_teams_join_meeting_id ??
          null,
        ms_teams_meeting_passcode:
          payloadAny.ms_teams_meeting_passcode ??
          existingInviteAny.ms_teams_meeting_passcode ??
          null,
        ...(platformRoomId !== null ? ({ platform_room_id: platformRoomId } as any) : {}),
        duration_minutes:
          payloadAny.duration_minutes ??
          existingInviteAny.duration_minutes ??
          null,
        scheduled_at: payload.scheduled_at || existingInvite.scheduled_at || new Date().toISOString()
      } as AiInterviewInvite;
      const updatedInvites = state.invites.map((i) =>
        i.id === existingInvite.id ? merged : i
      );
      await upsert_interview_invite({
        ...merged,
        candidate_id: merged.candidate_id,
        job_id: merged.job_id ?? null
      } as any);
      commit("SET_AI_INTERVIEW_INVITES", updatedInvites);
      return merged;
    }

    const platformRoomId = payloadAny.platform_room_id ?? null;
    const invite = {
      id: makeId(),
      interview_type,
      job_id: payload.job_id ?? null,
      job_title: payloadAny.job_title ?? null,
      job_role: payload.job_role ?? null,
      candidate_id: payload.candidate_id,
      upload_response_id: payload.upload_response_id ?? null,
      candidate_name: payload.candidate_name,
      candidate_role: payload.candidate_role,
      avatar_uri: payload.avatar_uri ?? null,
      interviewer_email: payload.interviewer_email ?? null,
      ms_teams_link: payload.ms_teams_link ?? null,
      ms_teams_meeting_id: payload.ms_teams_meeting_id ?? null,
      ms_teams_join_meeting_id: payloadAny.ms_teams_join_meeting_id ?? null,
      ms_teams_meeting_passcode: payloadAny.ms_teams_meeting_passcode ?? null,
      ...(platformRoomId !== null ? ({ platform_room_id: platformRoomId } as any) : {}),
      duration_minutes: payloadAny.duration_minutes ?? null,
      scheduled_at: payload.scheduled_at ?? new Date().toISOString(),
      status: "scheduled" as const
    } as AiInterviewInvite;

    commit("ADD_AI_INTERVIEW_INVITE", invite);
    const resp = await upsert_interview_invite({
      ...invite,
      candidate_id: invite.candidate_id,
      job_id: invite.job_id ?? null
    } as any);
    if (resp?.data?.data?.id) invite.id = String(resp.data.data.id);
    return invite;
  }
};

export default actions;

